/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import IntroSection from '../../common/IntroSection';
import MainSection from '../components/MainSection';
import NonFieldErrorBanner from '../components/NonFieldErrorBanner';
import TextInputField from '../components/TextInputField';
import { useFormControls } from '../../../common/hooks/useFormControls';
import {
  pressReleaseFormFields,
  pressContactFields,
  requiredField,
} from '../../helpers/pressReleaseConstants';
import { statesAndTerritories, countries } from '../../../common/helpers/geographicConstants';
import PressContactsInputSection from '../components/PressContactsInputSection';
import MiscSnippet from '../components/staticSnippets/MiscSnippet';
import CheckboxInputField from '../components/CheckboxInputField';
import MultimediaForm from '../components/MultimediaForm';
import PressReleaseService from '../services/pressReleaseService';
import { checkoutURL } from '../helpers/divewireConstants';
import PriceSummaryBox from '../components/PriceSummaryBox';
import PublishDateInputField from '../components/PublishDateInputField';
import FileInputField from '../components/FileInputField';
import displayBackendErrors from '../../../common/helpers/displayBackendErrors';
import PaymentIntentService from '../services/paymentIntentService';
import LoadingScreen from '../../../common/LoadingScreen';
import ConfirmModal from '../../../common/ConfirmModal';
import { scrollToTopOrError } from '../../../common/helpers/scrollTo';
import ContactForm from '../components/ContactForm';
import ManageLinks from '../components/ManageLinks';
import DiveMessageBanner from '../../common/DiveMessageBanner';
import {
  getPublishSuccessMessage,
  getScheduledPublishInfo,
} from '../helpers/timeHelpers';

import PRESS_RELEASE_STATUS from '../helpers/pressReleaseUtils';

const CreateScreen = () => {
  const formInitialized = useRef(false);
  // eslint-disable-next-line object-curly-newline
  const { pressRelease, dispatch, pressReleaseMetadata, promoteSites, nonFieldError, setNonFieldError } = useOutletContext();
  const navigate = useNavigate();
  const { token } = useParams();

  const {
    fields,
    getFormValues,
    updateForm,
    validateField,
    validateForm,
    setError,
    setValidators,
    resetField,
    getErroringFieldNames,
    setField,
    addGroupInstance,
    removeGroupInstance,
    getGroupInstances,
    getGroupValues,
    getGroupFields,
  } = useFormControls(pressReleaseFormFields, {
    fieldGroups: {
      press_contacts: {
        schema: pressContactFields,
        minInstances: 1,
        maxInstances: 5,
        initialData: pressRelease?.press_contacts || [],
      },
    },
  });
  const pressReleaseApi = new PressReleaseService(token);
  const paymentIntentApi = new PaymentIntentService(token);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [fileRejections, setFileRejections] = useState([]);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  /*
  TODO: reimplement subscribe to newsletter checkbox in ENGA-829
  const selfSubscribeLocalStorageKey = `${checkoutURL}/${token}/self_subscribe`;
  const [selfSignup, setSelfSignup] = useState(!!localStorage.getItem(selfSubscribeLocalStorageKey) ?? false);
  useEffect(() => {
    localStorage.setItem(selfSubscribeLocalStorageKey, selfSignup ? 'checked' : '');
  }, [selfSignup]);
  */

  const handleCompanyCountryChange = (e) => {
    // Clear out company state field if country selected is not US and remove any lingering required field errors
    if (e.target.value !== 'United States') {
      // Keep press release and useFormControls states in sync
      resetField('company_state_territory');
    }
    setField(e);
  };

  const handlePublishDateChange = (dateStr) => {
    const event = {
      target: {
        name: 'publish_date',
        value: dateStr || null,
      },
    };
    setField(event);
  };

  const submitFileInputValues = async () => {
    const formData = new FormData();
    const fileInputs = document.querySelectorAll('input.pressrelease-file-input[type="file"]');
    let fileInputsFilled = false;
    fileInputs.forEach((el) => {
      if (el.files[0]) {
        formData.append(el.name, el.files[0] ?? {});
        fileInputsFilled = true;
      }
    });
    if (fileInputsFilled) {
      await pressReleaseApi.updatePressRelease(formData);
    }
  };

  const validateFormData = () => {
    const isValid = validateForm();
    const erroringFieldNames = getErroringFieldNames();

    if (erroringFieldNames.length > 0) {
      scrollToTopOrError(erroringFieldNames);
      return false;
    }
    return isValid;
  };

  const submitApiData = async () => {
    await submitFileInputValues();

    const mainFormValues = getFormValues();
    const pressContacts = getGroupValues('press_contacts')
      .filter((contact) => contact.name?.trim() || contact.email?.trim() || contact.phone_number?.trim())
      .map((contact) => ({
        name: contact.name?.trim(),
        email: contact.email?.trim(),
        phone_number: contact.phone_number?.trim(),
      }));

    const apiPayload = {
      ...mainFormValues,
      // eslint-disable-next-line camelcase
      press_contacts: pressContacts,
    };

    const prResponse = await pressReleaseApi.updatePressRelease(apiPayload);

    let paymentIntentResponse = null;
    if (prResponse.data.press_release.status !== 'PAID') {
      paymentIntentResponse = await paymentIntentApi.updatePaymentIntent();
    }

    return { prResponse, paymentIntentResponse };
  };

  const handleReviewSuccess = (prResponse, paymentIntentResponse) => {
    const hasValidPaymentFlow = pressRelease?.status !== PRESS_RELEASE_STATUS.PAID ? !!paymentIntentResponse : true;

    if (prResponse && hasValidPaymentFlow) {
      dispatch({
        type: 'SET_PRESS_RELEASE',
        payload: prResponse.data.press_release,
      });
      setNonFieldError('');
      navigate(`${checkoutURL}/${token}/review`);
    }
  };

  const handlePublishSuccess = (prResponse, paymentIntentResponse) => {
    const hasValidPaymentFlow = pressRelease?.status !== PRESS_RELEASE_STATUS.PAID ? !!paymentIntentResponse : true;

    if (prResponse && hasValidPaymentFlow) {
      dispatch({
        type: 'SET_PRESS_RELEASE',
        payload: prResponse.data.press_release,
      });
      setNonFieldError('');
      // Scroll to top and show success banner
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setShowPublishSuccess(true);
    }
  };

  const handleApiError = (apiError) => {
    displayBackendErrors(apiError, setError, setNonFieldError, pressReleaseFormFields);
    const response = apiError?.response?.data;
    const backendErrorFields = Object.keys(response || {});
    scrollToTopOrError(backendErrorFields);
  };

  const handleSubmit = async (successHandler) => {
    setIsSubmitting(true);
    try {
      // 1. Validate form
      if (!validateFormData()) {
        return;
      }

      // 2. Submit data to API
      const { prResponse, paymentIntentResponse } = await submitApiData();

      // 3. Handle successful submission with provided handler
      successHandler(prResponse, paymentIntentResponse);
    } catch (apiError) {
      handleApiError(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublish = async (e) => {
    e.persist();
    e.preventDefault();
    await handleSubmit(handlePublishSuccess);
  };

  const handleSaveReview = async (e) => {
    e.persist();
    e.preventDefault();
    await handleSubmit(handleReviewSuccess);
  };

  const handleRemove = async (e) => {
    e.persist();
    e.preventDefault();
    setIsRemoveModalOpen(true);
  };

  const confirmRemove = async () => {
    setIsRemoveModalOpen(false);
    setIsSubmitting(true);
    try {
      const response = await pressReleaseApi.deletePressRelease();
      if (response.status === 200) {
        window.location.replace('/press-release/');
      } else {
        handleApiError(response);
      }
    } catch (apiError) {
      handleApiError(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const setMultimedia = (multimedia) => {
    dispatch({
      type: 'SET_MULTIMEDIA',
      payload: multimedia,
    });
  };

  // Multimedia handlers for controlled component
  const handleFileUpload = (newFiles) => {
    const currentFiles = pressRelease.multimedia_add_ons || [];
    const updatedFiles = [...currentFiles, ...newFiles];
    setMultimedia(updatedFiles);
  };

  const handleFileRemove = (addOnId) => {
    const currentFiles = pressRelease.multimedia_add_ons || [];
    const updatedFiles = currentFiles.filter((addOn) => addOn.id !== addOnId);
    setMultimedia(updatedFiles);
  };

  const handleCaptionUpdate = (addOnId, caption) => {
    const currentFiles = pressRelease.multimedia_add_ons || [];
    const updatedFiles = currentFiles.map((addOn) => {
      if (addOn.id === addOnId) {
        return { ...addOn, caption };
      }
      return addOn;
    });
    setMultimedia(updatedFiles);
  };

  const handleFileRejectionAdd = (newRejections) => {
    setFileRejections((current) => [...current, ...newRejections]);
  };

  const handleFileRejectionRemove = (rejectionToRemove) => {
    setFileRejections((current) => current.filter((rejection) => rejection.fileName !== rejectionToRemove.fileName || rejection.error !== rejectionToRemove.error));
  };

  const handleFileRejectionsReset = () => {
    setFileRejections([]);
  };

  const setDependentValidators = () => {
    if (pressRelease?.status === PRESS_RELEASE_STATUS.PAID) {
      setValidators('accept_content_policy', []);
    }
    if (pressRelease.company_country === 'United States') {
      setValidators('company_state_territory', [requiredField]);
    } else {
      setValidators('company_state_territory', []);
    }
  };

  useEffect(() => {
    if (pressRelease && !formInitialized.current) {
      updateForm(pressRelease);
      setDependentValidators();
      formInitialized.current = true;
    }
  }, [pressRelease]);

  const pressReleasePaid = pressRelease?.status === PRESS_RELEASE_STATUS.PAID;
  const rowClassName = pressReleasePaid ? 'row flex-center' : 'row';
  const columnClassName = pressReleasePaid ? 'large-8 columns' : 'large-7 columns';
  const multimediaAddOnPriceInDollars = pressReleaseMetadata.multimedia_add_on_price / 100;

  return (
    <>
      <main id="skip-link-target" className={`pressrelease-create-screen${pressReleasePaid ? ' edit-screen' : ''}`}>
        <div className={rowClassName}>
          <div className={`columns${pressReleasePaid ? ' large-8' : ''}`}>
            { pressReleasePaid
              ? (
                <IntroSection title="Manage Press Release" />
              ) : (
                <IntroSection title="Create a Press Release">
                  To post your press release to {pressReleaseMetadata.current_site_name}, fill out the form below.
                  Our self-serve platform allows you to publish your news immediately, or plan for it to go live at a scheduled date.
                </IntroSection>
              )}
          </div>
        </div>
        {showPublishSuccess && (
          <div className={rowClassName}>
            <div className={columnClassName}>
              <DiveMessageBanner
                messageType="success"
                onClose={() => setShowPublishSuccess(false)}
              >
                {getPublishSuccessMessage(pressRelease.publish_date)}
              </DiveMessageBanner>
            </div>
          </div>
        )}
        {pressReleasePaid && (
          <>
            <div className={rowClassName}>
              <div className={columnClassName}>
                <ManageLinks pressRelease={pressRelease} siteList={promoteSites.sitesList} />
              </div>
            </div>
            <div className={rowClassName}>
              <div className={columnClassName}>
                <ContactForm
                  fields={fields}
                  setField={setField}
                  validateField={validateField}
                  nonFieldError={nonFieldError}
                  setNonFieldError={setNonFieldError}
                  pressReleaseMetadata={pressReleaseMetadata}
                />
              </div>
            </div>
          </>
        )}
        <div className={rowClassName}>
          <div className={columnClassName}>
            <MainSection className="press-release-content" title="Press release content">
              <TextInputField
                className="press-release-title"
                id="title"
                label="Press release title"
                isRequired
                onChange={setField}
                onBlur={validateField}
                value={fields.title.value}
                errorMsg={fields.title.errors}
                touched={fields.title.touched}
              />

              <TextInputField
                className="press-release-subheader"
                id="subheader"
                label="Subheadline"
                maxLength={150}
                characterCounter
                onChange={setField}
                onBlur={validateField}
                value={fields.subheader.value}
                errorMsg={fields.subheader.errors}
                touched={fields.subheader.touched}
              />

              <MiscSnippet name="divewire_content_policy" />

              <TextInputField
                className="press-release-text"
                id="full_text"
                label="Press release"
                type="textarea"
                isRequired
                onChange={setField}
                onBlur={validateField}
                value={fields.full_text.value}
                errorMsg={fields.full_text.errors}
                touched={fields.full_text.touched}
              />

              <TextInputField
                className="press-release-about"
                id="company_description"
                label="About"
                type="textarea"
                isRequired
                onChange={setField}
                onBlur={validateField}
                value={fields.company_description.value}
                errorMsg={fields.company_description.errors}
                touched={fields.company_description.touched}
              />
            </MainSection>
          </div>
        </div>

        {/* TODO: remove this condition once we have a way to purchase multimedia add-ons on paid press releases */}
        {(!pressReleasePaid || pressRelease.has_multimedia_add_on) && (
          <div className={rowClassName}>
            <div className={columnClassName}>
              <MainSection className="multi-media-gallery" title="Multi-media gallery">
                <div className="explanation-text">
                  {!pressReleasePaid && `Enhance your press release page by adding visual elements for just $${multimediaAddOnPriceInDollars}. `}
                  Add up to five photos, videos, or files to help readers gain context about your announcement.
                </div>
                {/* TODO: reimplement subscribe to newsletter checkbox in ENGA-829
                <CheckboxInputField
                  id="self_subscribe"
                  label={`Subscribe to the ${pressReleaseMetadata.current_site_name} daily newsletter`}
                  description="View your press release in the Company Announcement
                    section of our daily newsletter."
                  onChange={(event) => setSelfSignup(event.target.checked)}
                  checked={selfSignup}
                /> */}
                <MultimediaForm
                  pressReleaseId={pressRelease.id}
                  multimediaAddOns={pressRelease.multimedia_add_ons || []}
                  onFileUpload={handleFileUpload}
                  onFileRemove={handleFileRemove}
                  onCaptionUpdate={handleCaptionUpdate}
                  fileRejections={fileRejections}
                  onFileRejectionAdd={handleFileRejectionAdd}
                  onFileRejectionRemove={handleFileRejectionRemove}
                  onFileRejectionsReset={handleFileRejectionsReset}
                />
              </MainSection>
            </div>
          </div>
        )}

        <div className={rowClassName}>
          <div className={columnClassName}>
            <MainSection className="press-contact-information" title="Press contact information">
              <PressContactsInputSection
                submitterName={fields.submitter_name.value}
                submitterEmail={fields.submitter_email.value}
                contactInstances={getGroupInstances('press_contacts')}
                getGroupFields={getGroupFields}
                addContact={() => addGroupInstance('press_contacts')}
                removeContact={(id) => removeGroupInstance('press_contacts', id)}
                setField={setField}
                validateField={validateField}
              />
            </MainSection>
          </div>
        </div>

        <div className={rowClassName}>
          <div className={columnClassName}>
            <MainSection className="company-information" title="Company information">
              <TextInputField
                className="press-release-company-name"
                id="company_name"
                label="Company name"
                isRequired
                onChange={setField}
                onBlur={validateField}
                value={fields.company_name.value}
                errorMsg={fields.company_name.errors}
                touched={fields.company_name.touched}
              />
              <TextInputField
                className="press-release-company-url"
                id="company_url"
                label="Company URL"
                onChange={setField}
                onBlur={validateField}
                value={fields.company_url.value}
                errorMsg={fields.company_url.errors}
                touched={fields.company_url.touched}
              />
              <div className="company-location row">
                <div className="company-location__header">
                  Company HQ location<span className="form__required-tag">*</span>
                </div>
                <div className="row">
                  <div className="column large-4 medium-12 company-location__field">
                    <label htmlFor="company_city">
                      <span className="company-location__label">City</span>
                      <input
                        type="text"
                        name="company_city"
                        maxLength="200"
                        id="company_city"
                        style={{ marginBottom: '0px' }}
                        onChange={setField}
                        onBlur={validateField}
                        value={fields.company_city.value}
                      />
                    </label>
                    {fields.company_city.touched
                      && fields.company_city.errors.map((error) => (
                        <p key={error} className="form-error__message">
                          {error}
                        </p>
                      ))}
                  </div>
                  <div className="column large-4 medium-12 company-location__field">
                    <span className="company-location__label">Country</span>
                    <select
                      name="company_country"
                      id="company_country"
                      aria-label="Enter your company country"
                      onChange={handleCompanyCountryChange}
                      onBlur={validateField}
                      value={fields.company_country.value}
                    >
                      <option value="" aria-label="None selected" />
                      {countries.map((country) => {
                        return (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        );
                      })}
                    </select>
                    {fields.company_country.touched
                      && fields.company_country.errors.map((error) => (
                        <p key={error} className="form-error__message">
                          {error}
                        </p>
                      ))}
                  </div>
                  <div
                    className="column large-4 medium-12 company-location__field"
                    style={{ opacity: fields.company_country.value === 'United States' ? 1 : 0.6 }}
                  >
                    <span className="company-location__label">State/Territory</span>
                    <select
                      disabled={fields.company_country.value !== 'United States'}
                      name="company_state_territory"
                      id="company_state_territory"
                      aria-label="Enter your company's state or territory"
                      onChange={setField}
                      onBlur={validateField}
                      value={fields.company_state_territory.value}
                    >
                      <option value="" aria-label="None selected" />
                      {statesAndTerritories.map((state) => {
                        return (
                          <option key={state.value} value={state.value}>
                            {state.label}
                          </option>
                        );
                      })}
                    </select>
                    {fields.company_state_territory.touched
                      && fields.company_state_territory.errors.map((error) => (
                        <p key={error} className="form-error__message">
                          {error}
                        </p>
                      ))}
                  </div>
                </div>
              </div>

              <FileInputField
                id="cloudinary_company_logo"
                label="Square company logo"
                onChange={setField}
                setError={(msg) => setError('cloudinary_company_logo', msg)}
                value={fields.cloudinary_company_logo.value}
                errorMsg={fields.cloudinary_company_logo.errors}
                touched={fields.cloudinary_company_logo.touched}
                maxSizeInBytes={500 * 1024} // 500KB
                validFileTypes={['image/jpeg', 'image/png']}
              />

              <FileInputField
                id="cloudinary_header_image"
                label="Header image"
                onChange={setField}
                setError={(msg) => setError('cloudinary_header_image', msg)}
                value={fields.cloudinary_header_image.value}
                errorMsg={fields.cloudinary_header_image.errors}
                touched={fields.cloudinary_header_image.touched}
                maxSizeInBytes={1024 * 1024} // 1MB
                validFileTypes={['image/jpeg', 'image/png']}
              />

              {!pressReleasePaid && (
                <>
                  <PublishDateInputField
                    value={fields.publish_date.value}
                    onChange={handlePublishDateChange}
                  />
                  <PriceSummaryBox
                    pressRelease={pressRelease}
                    pressReleaseMetadata={pressReleaseMetadata}
                    sitesList={promoteSites.sitesList}
                  />
                  <CheckboxInputField
                    id="accept_content_policy"
                    label={(
                      <>
                        I accept the&nbsp;
                        <a href="https://industrydive.com/divewire-content-policy/" target="_blank" rel="noreferrer">
                          DiveWire Content Guidelines
                        </a>
                      </>
                    )}
                    description="Please review and accept our policy to confirm your press
                      release meets the DiveWire content guidelines."
                    isRequired
                    onChange={setField}
                    onBlur={validateField}
                    checked={fields.accept_content_policy.value}
                    errorMsg={fields.accept_content_policy.errors}
                    touched={fields.accept_content_policy.touched}
                  />
                </>
              )}

              {nonFieldError && <NonFieldErrorBanner setErrorCallback={setNonFieldError} prContactEmail={pressReleaseMetadata.pressrelease_contact_email} />}
            </MainSection>
          </div>
        </div>

        {(() => {
          const scheduledInfo = getScheduledPublishInfo(pressRelease.publish_date);
          if (!pressReleasePaid || !scheduledInfo) return null;

          return (
            <div className={rowClassName}>
              <div className={columnClassName}>
                <PublishDateInputField
                  value={pressRelease.publish_date}
                  onChange={handlePublishDateChange}
                  isPaid={pressReleasePaid}
                />
              </div>
            </div>
          );
        })()}

        <div className={rowClassName}>
          <div className={columnClassName}>
            <section className="button-container">
              {pressReleasePaid && (
                <button
                  id="form-publish-pr"
                  type="submit"
                  className="button button--medium"
                  onClick={handlePublish}
                >
                  Publish
                </button>
              )}
              <button
                id="form-review-pr"
                type="submit"
                className="button button--medium"
                onClick={handleSaveReview}
              >
                Save and review
              </button>
              {pressReleasePaid && (
                <button
                  id="form-remove-pr"
                  type="submit"
                  className="button button--soft"
                  onClick={handleRemove}
                >
                  Remove
                </button>
              )}
            </section>
          </div>
        </div>
      </main>
      <ConfirmModal
        isOpen={isRemoveModalOpen}
        onConfirm={confirmRemove}
        onCancel={() => setIsRemoveModalOpen(false)}
      >
        <h3 className="confirm-modal__heading">
          Are you sure you want to remove this press
          <br />
          release? The press release will be removed from all associated publications.
        </h3>
        <div className="confirm-modal__button-wrapper">
          <button id="remove-press-release-yes" type="button" className="button button--medium" onClick={confirmRemove}>
            Yes
          </button>
          <p className="confirm-modal__cancel-container">
            <button id="remove-press-release-no" type="button" className="remove-press-release-no" onClick={() => setIsRemoveModalOpen(false)}>
              Cancel
            </button>
          </p>
        </div>
      </ConfirmModal>
      {isSubmitting && <LoadingScreen isSubmitting={isSubmitting} />}
    </>
  );
};

export default CreateScreen;
